import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { ICONS } from '@/constants/icons';
import { SHOP_PAYMENT_METHODS_URL } from '@/constants/shop';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { esActivationPath, formatEsMoney, isSafePayUrl } from '@/utils/earlySupporterFormat';
import { useEarlySupporterStore } from '@/stores/earlySupporterStore';
import { useTranslation } from 'react-i18next';
import { useCancelEs, useResumeEs, useSwitchEsToYearly } from '@/hooks/useEarlySupporter';
import type { EsEntitlementStatus, EsEntitlementView } from '@/types/earlySupporter';
import { EmailNotVerifiedNotice } from '@/components/shop/EmailNotVerifiedNotice';
import { isEmailNotVerifiedError } from '@/utils/emailNotVerified';
import { EsConfirm } from './EsConfirm';

const STATUS_STYLE: Record<EsEntitlementStatus, { key: string; className: string }> = {
  ACTIVE: { key: 'early_supporter_status_active', className: 'bg-green-100 text-green-700 border-green-200' },
  PAST_DUE: { key: 'early_supporter_status_past_due', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  CANCEL_PENDING: { key: 'early_supporter_status_cancel_pending', className: 'bg-gray-100 text-gray-600 border-gray-200' },
};

interface EsManagePanelProps {
  entitlement: EsEntitlementView;
  currency?: string;
}

type Dialog = 'cancel' | 'switch' | null;

export const EsManagePanel = ({ entitlement, currency = 'EUR' }: EsManagePanelProps) => {
  const { t, i18n } = useTranslation('settings');
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const { formatDateOnly } = useTimezone();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const cancelMutation = useCancelEs();
  const resumeMutation = useResumeEs();
  const switchMutation = useSwitchEsToYearly();

  const status = STATUS_STYLE[entitlement.status];
  const endDate = entitlement.accessUntil ?? entitlement.currentPeriodEnd;
  const endLabel = endDate ? formatDateOnly(endDate) : '';
  const isCancelPending = entitlement.status === 'CANCEL_PENDING';
  const canResume = entitlement.canResume ?? isCancelPending;
  const nextPayment = !isCancelPending ? entitlement.nextPayment : null;

  const handleCancel = () => {
    cancelMutation.mutate(undefined, {
      onSuccess: () => {
        setDialog(null);
        toast.success(t('early_supporter_cancel_done'));
      },
      onError: () => toast.error(t('early_supporter_error_generic')),
    });
  };

  const handleResume = () => {
    resumeMutation.mutate(undefined, {
      onSuccess: () => toast.success(t('early_supporter_resume_done')),
      onError: () => toast.error(t('early_supporter_error_generic')),
    });
  };

  const handleSwitch = () => {
    switchMutation.mutate(
      {},
      {
        onSuccess: (result) => {
          if (result.completed === true) {
            // EUR 0 switch: completed server-side; Billing polls the checkout and finalizes the switch.
            useEarlySupporterStore.getState().reset();
            setDialog(null);
            navigate(esActivationPath(result.checkoutId), { replace: true });
            return;
          }
          const { payUrl } = result;
          if (isSafePayUrl(payUrl)) {
            // Draft is irrelevant after the switch; the shop page takes over from here.
            useEarlySupporterStore.getState().reset();
            window.location.assign(payUrl);
          } else toast.error(t('early_supporter_error_generic'));
        },
        onError: (error) => {
          if (isEmailNotVerifiedError(error)) {
            setDialog(null);
            setEmailNotVerified(true);
            return;
          }
          toast.error(t('early_supporter_error_generic'));
        },
      },
    );
  };

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
      aria-labelledby="es-manage-title"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary-400 to-secondary-500" />
      <div className="p-5 sm:p-6 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h3 id="es-manage-title" className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-900">
            <Icon name={ICONS.STAR} size="sm" color="text-primary-600" ariaHidden />
            {t('early_supporter_manage_title')}
          </h3>
          <span className={cn('inline-flex px-3 py-1 rounded-full text-xs font-semibold border', status.className)}>
            {t(status.key)}
          </span>
        </div>

        {entitlement.status === 'PAST_DUE' && (
          <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <Icon name={ICONS.CAUTION} size="md" color="text-amber-600" ariaHidden />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">{t('early_supporter_past_due_title')}</p>
              <p className="text-sm text-amber-800">{t('early_supporter_past_due_body')}</p>
            </div>
            <a
              href={SHOP_PAYMENT_METHODS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-10 px-4 rounded-lg bg-white border border-amber-300 text-sm font-semibold text-amber-900 hover:bg-amber-100 transition-colors"
            >
              {t('early_supporter_past_due_cta')}
            </a>
          </div>
        )}

        {isCancelPending && endLabel && (
          <p className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
            {t('early_supporter_cancel_pending_note', { date: endLabel, returnsTo: entitlement.revertsToPackage.name })}
          </p>
        )}

        {nextPayment && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-primary-200 bg-primary-50/50 px-4 py-3">
            <span className="text-sm font-semibold text-gray-800">{t('early_supporter_manage_next_payment')}</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatEsMoney(nextPayment.gross, currency, locale)}{' '}
              <span className="font-normal text-gray-600">{t('early_supporter_manage_incl_vat')}</span>
              {nextPayment.date ? <span className="ml-1 font-normal text-gray-600">· {formatDateOnly(nextPayment.date)}</span> : null}
            </span>
          </div>
        )}

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wider text-gray-400 mb-1">{t('early_supporter_manage_plan')}</dt>
            <dd className="text-sm font-medium text-gray-900">{entitlement.package.name}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-gray-400 mb-1">{t('early_supporter_manage_billing')}</dt>
            <dd className="text-sm font-medium text-gray-900">
              {entitlement.billingPeriod === 'YEAR' ? t('early_supporter_period_year') : t('early_supporter_period_month')}
            </dd>
          </div>
          {!isCancelPending && entitlement.promoPaymentsLeft !== null && entitlement.promoPaymentsLeft > 0 && (
            <div>
              <dt className="text-xs uppercase tracking-wider text-gray-400 mb-1">{t('early_supporter_manage_promo_left')}</dt>
              <dd className="text-sm font-medium text-gray-900">{entitlement.promoPaymentsLeft}</dd>
            </div>
          )}
          {isCancelPending && endLabel && (
            <div>
              <dt className="text-xs uppercase tracking-wider text-gray-400 mb-1">{t('early_supporter_manage_access_until')}</dt>
              <dd className="text-sm font-medium text-gray-900">{endLabel}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs uppercase tracking-wider text-gray-400 mb-1">{t('early_supporter_manage_returns_to')}</dt>
            <dd className="text-sm font-medium text-gray-900">{entitlement.revertsToPackage.name}</dd>
          </div>
        </dl>

        {emailNotVerified && <EmailNotVerifiedNotice className="rounded-lg border border-amber-200 bg-amber-50 p-3" />}

        <div className="flex flex-col sm:flex-row gap-2 pt-1 border-t border-gray-100 sm:pt-4">
          {isCancelPending ? (
            canResume && (
              <Button size="md" loading={resumeMutation.isPending} onClick={handleResume}>
                {t('early_supporter_manage_resume')}
              </Button>
            )
          ) : (
            <>
              {entitlement.canSwitchToYearly && entitlement.billingPeriod === 'MONTH' && (
                <Button variant="secondary" size="md" onClick={() => setDialog('switch')}>
                  {t('early_supporter_manage_switch_yearly')}
                </Button>
              )}
              <Button variant="outline" size="md" onClick={() => setDialog('cancel')}>
                {t('early_supporter_manage_cancel')}
              </Button>
            </>
          )}
        </div>
      </div>

      <EsConfirm
        open={dialog === 'cancel'}
        onOpenChange={(open) => !open && setDialog(null)}
        title={t('early_supporter_cancel_title')}
        description={t('early_supporter_cancel_body', {
          plan: entitlement.package.name,
          date: endLabel,
          returnsTo: entitlement.revertsToPackage.name,
        })}
        confirmText={t('early_supporter_cancel_confirm')}
        cancelText={t('early_supporter_cancel_keep')}
        variant="danger"
        loading={cancelMutation.isPending}
        onConfirm={handleCancel}
      />
      <EsConfirm
        open={dialog === 'switch'}
        onOpenChange={(open) => !open && setDialog(null)}
        title={t('early_supporter_switch_title')}
        description={t('early_supporter_switch_body')}
        confirmText={t('early_supporter_switch_confirm')}
        cancelText={t('early_supporter_switch_cancel')}
        loading={switchMutation.isPending}
        onConfirm={handleSwitch}
      />
    </section>
  );
};
